import { CommonModule } from '@angular/common';
import { Component, OnChanges, OnDestroy } from '@angular/core';
import { ViewChild } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Input, ElementRef } from '@angular/core';

import { languageService, languageSupport } from '@unpispas/upp-base';
import { ViewRenderer } from '@unpispas/upp-wdgt';

import { dataService } from '@unpispas/upp-data';
import { TicketView } from '@unpispas/upp-data';
import { TicketProductView } from '@unpispas/upp-data';
import { PrintDevice } from '@unpispas/upp-data';
import { OrderAction } from '@unpispas/upp-data';

export type { OrderAction };

/********************************/
/* Public order print model     */
/********************************/

type OrderPrintLine = {
    isSeparator: boolean;
    count: number;
    name: string;
    comments: string | null;
    weight: number | null;
};

export type OrderPrintInfo = {
    action: OrderAction;
    order: string;
    table: number | null;
    diners: number | null;
    comments: string | null;
    time: string;
    additions: OrderPrintLine[];
    removals: OrderPrintLine[];
};

/********************************/
/* Data calculations            */
/********************************/

/**
 * Maps a `TicketView` plus destination printer **configuration** into a printable
 * order document. Line selection uses `GetNotified` (`live − notified`) and
 * printer config (`type`, `updates`, SPC catalog) — not a change-accumulation buffer.
 */
class OrderToPrint {
    private _info: OrderPrintInfo | null = null;

    constructor(
        private ticket: TicketView,
        private printer: PrintDevice,
        private action: OrderAction,
        private lang: string,
        private data: dataService,
    ) {}

    info(refresh = false): OrderPrintInfo {
        if (refresh || (this._info === null)){
            this._info = this.BuildInfo();
        }

        return this._info;
    }

    /**
     * Product lines included in this destination's slip (SetNotified targets).
     * Separators are excluded. For `set`, only lines with non-zero pending delta.
     * For `copy` / `new` / `del`, all routed preparation lines.
     */
    CoveredLines(): Array<TicketProductView> {
        const _source = this.action === 'set'
            ? this.OrderCandidates()
            : this.ticket.ProductsToPrepare;

        return _source.filter((line) => {
            if (line.IsSplitter || !this.IncludesLine(line)){
                return false;
            }

            if (this.action === 'set'){
                return line.GetNotified() !== 0;
            }

            return true;
        });
    }

    private BuildInfo(): OrderPrintInfo {
        const _source = this.action === 'set'
            ? this.OrderCandidates()
            : this.ticket.ProductsToPrepare;
        const _routed = _source.filter((line) => line.IsSplitter || this.IncludesLine(line));

        const _header = {
            action: this.action,
            order: String(this.ticket.orderno || '').trim(),
            table: this.ticket.qrcode?.number ?? null,
            diners: this.ticket.diners ?? null,
            comments: String(this.ticket.comments || '').trim() || null,
            time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        };

        if (this.action === 'del'){
            return {
                ..._header,
                additions: [],
                removals: _routed.map((line) => (
                    line.IsSplitter ? this.BuildSeparator() : this.BuildLine(line, line.Count)
                )),
            };
        }

        if (this.action === 'set'){
            const _additions: OrderPrintLine[] = [];
            const _removals: OrderPrintLine[] = [];

            for (const line of _routed){
                if (line.IsSplitter){
                    _additions.push(this.BuildSeparator());
                    continue;
                }

                const _delta = line.GetNotified();
                if (_delta > 0){
                    _additions.push(this.BuildLine(line, _delta));
                }
                else if (_delta < 0){
                    _removals.push(this.BuildLine(line, _delta));
                }
            }

            return {
                ..._header,
                additions: _additions,
                removals: _removals,
            };
        }

        // 'new' and 'cpy': full preparation slip as additions
        return {
            ..._header,
            additions: _routed.map((line) => (
                line.IsSplitter ? this.BuildSeparator() : this.BuildLine(line, line.Count)
            )),
            removals: [],
        };
    }

    private OrderCandidates(): Array<TicketProductView> {
        return this.ticket.PrintOrder;
    }

    private IncludesLine(line: TicketProductView): boolean {
        if (!this.printer.IsValid){
            return false;
        }

        switch (this.printer.type){
            case 'BAR':
                return (this.printer.updates === true) && (line.type !== 'KIT');

            case 'KIT':
                return line.type === 'KIT';

            case 'SPC': {
                const _line = line.product;
                if (!_line){
                    return false;
                }

                return this.printer.products.some((printerproduct) => {
                    if (!printerproduct.IsValid || !printerproduct.product){
                        return false;
                    }

                    if (!_line.IsChild(printerproduct.product)){
                        return false;
                    }

                    const _filteropts = printerproduct.options.filter(
                        (printeroption) => printeroption.IsValid && !!printeroption.option,
                    );
                    if (_filteropts.length === 0){
                        return true;
                    }

                    return _filteropts.some((printeroption) =>
                        line.ticketoptions.some(
                            (ticketoption) => ticketoption.IsValid && (ticketoption.option === printeroption.option),
                        ),
                    );
                });
            }

            case 'GEN':
                return this.printer.updates === true;

            default:
                return false;
        }
    }

    private BuildSeparator(): OrderPrintLine {
        return { isSeparator: true, count: 0, name: '', comments: null, weight: null };
    }

    private BuildLine(line: TicketProductView, count: number): OrderPrintLine {
        return {
            isSeparator: false,
            count: Math.abs(count),
            name: line.label?.tr(this.lang) || '',
            comments: String(line.comments || '').trim() || null,
            weight: line.product?.IsWeighted ? Number(line.weight || 0) : null,
        };
    }
}

/********************************/
/* UppTicketOrderComponent      */
/********************************/

/**
 * Order slip host: renders real Angular DOM only, one destination printer at a time.
 * Preview uses `display=true` with a single `printer`/`action`.
 * Productive dispatch: {@link queueService} resolves which printers must receive the
 * order and calls {@link Printable} once per printer, then `printService`. This component
 * never resolves printer routing and never opens WS or calls print itself.
 */
@Component({
    selector: 'upp-ticket-order',
    standalone: true,
    imports: [CommonModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './upp-print.html',
    styleUrls: ['./upp-print.scss'],
    providers: [{
        provide: ViewRenderer,
        useFactory: (change: ChangeDetectorRef) => new ViewRenderer(change),
        deps: [ChangeDetectorRef],
    }],
})
export class UppTicketOrderComponent extends languageSupport implements OnChanges, OnDestroy {
    @Input() ticket: TicketView | null = null;
    /** When true, render a single visible slip for preview (`printer` + `action`). */
    @Input() display = false;
    @Input() printer: PrintDevice | null = null;
    @Input() action: OrderAction = 'new';

    @ViewChild('printable', { read: ElementRef }) private _printable: ElementRef<HTMLElement> | null = null;

    private _orderToPrint: OrderToPrint | null = null;
    private _previewInfo: OrderPrintInfo | null = null;
    private _dispatchInfo: OrderPrintInfo | null = null;
    private _dispatchLines: Array<TicketProductView> | null = null;

    constructor(
        lang: languageService,
        private change: ViewRenderer,
        private data: dataService,
    ) {
        super(lang, change.cdref);
    }

    ngOnChanges(): void {
        if (this.display){
            this._orderToPrint = null;
            this.Refresh();
        }
    }

    ngOnDestroy(): void {
        this.IsPrinting = false;
        super.OnDestroy();
    }

    get Info(): OrderPrintInfo | null {
        return this._previewInfo;
    }

    get DispatchInfo(): OrderPrintInfo | null {
        return this._dispatchInfo;
    }

    /** Lines covered by the slip currently held in `DispatchInfo`, for `SetNotified` after dispatch. */
    get DispatchLines(): Array<TicketProductView> | null {
        return this._dispatchLines;
    }

    private _printing = false;

    get IsPrinting(): boolean {
        return this._printing;
    }

    /**
     * `true` while the silent single-slip dispatch is open (between `Printable` and jobs
     * setting it back to `false`). Same "render only what's needed" pattern as
     * `upp-ticket-print.IsPrinting`: setting it back to `false` also drops `DispatchInfo`/
     * `DispatchLines`, releasing the rendered slip.
     */
    set IsPrinting(value: boolean) {
        this._printing = value;
        if (!value){
            this._dispatchInfo = null;
            this._dispatchLines = null;
        }
        this.change.markForCheck();
    }

    /**
     * Renders one order slip for `printer` and returns the rendered `nativeElement` for
     * {@link queueService} to serialize and dispatch, or `null` when `printer` has no pending
     * lines to print. The caller resolves which printers must receive the order and calls this
     * once per destination, sequentially, reading `DispatchLines` right after this resolves
     * (before the next `Printable` call) for `SetNotified`; it must set `IsPrinting = false`
     * once every destination has been rendered.
     *
     * Forces `detectChanges()` synchronously instead of waiting on an `AfterViewChecked` signal:
     * Angular's `detectChanges()` already updates the view (including the `*ngIf` dispatch host)
     * before returning, so there is nothing to await — waiting on a lifecycle hook here only adds
     * a race between the hook firing and the wait subscribing, with no way to recover if it never
     * fires for this dynamically hosted component.
     */
    async Printable(printer: PrintDevice, action: OrderAction): Promise<HTMLElement | null> {
        const _ticket = this.ticket;
        if (!_ticket || this.display){
            return null;
        }

        const _order = new OrderToPrint(_ticket, printer, action, _ticket.place?.lang || 'ES', this.data);
        const _lines = _order.CoveredLines();
        if (!_lines.length){
            return null;
        }

        this._dispatchInfo = _order.info(true);
        this._dispatchLines = _lines;
        this.IsPrinting = true;
        this.change.markForCheck(true);

        const _element = this._printable?.nativeElement;
        if (!_element){
            console.error('[TICKET ORDER] printable host missing after view refresh');
            return null;
        }

        return _element;
    }

    private Refresh(): void {
        if (!this.ticket || !this.printer){
            this._previewInfo = null;
            this.change.markForCheck();
            return;
        }

        if (!this._orderToPrint){
            this._orderToPrint = new OrderToPrint(
                this.ticket,
                this.printer,
                this.action,
                this.ticket.place?.lang || 'ES',
                this.data,
            );
        }

        this._previewInfo = this._orderToPrint.info(true);
        this.change.markForCheck();
    }
}
